import type { Permission } from '../rbac.js';
import {
  REPORT_CATEGORIES,
  type ReportCategoryId,
  type ReportColumnDef,
  type ReportColumnType,
  type CatalogueReport,
  type CatalogueExportFormat,
  type ReportStatus,
} from './types.js';

/**
 * The report catalogue: 81 standard reports in a fixed order, plus
 * WHNUNIVERSE's own additions, which are never counted among the 81.
 *
 * Order matters and is asserted by tests: categories appear in the order of
 * `REPORT_CATEGORIES`, and reports within a category in the order listed here.
 *
 * Status is derived from the three sets at the bottom. A report is only in
 * `TESTED_REPORTS` when a test that names its id executes it.
 */

const col = (key: string, label: string, type: ReportColumnType = 'text'): ReportColumnDef => ({
  key,
  label,
  type,
});
const money = (key: string, label: string) => col(key, label, 'money');
const num = (key: string, label: string) => col(key, label, 'number');
const date = (key: string, label: string) => col(key, label, 'date');
const status = (key: string, label: string) => col(key, label, 'status');

const ALL_EXPORTS: CatalogueExportFormat[] = ['csv', 'xlsx', 'pdf'];

type Spec = Omit<
  CatalogueReport,
  'category' | 'categoryPosition' | 'position' | 'catalogueNumber' | 'status' | 'exports' | 'testRef' | 'orientation'
> & { orientation?: 'portrait' | 'landscape' };

const INV: Permission[] = ['invoice:read'];

/** Reports exactly as ordered in the catalogue. */
const ORDERED: Record<Exclude<ReportCategoryId, 'whn_books'>, Spec[]> = {
  business_overview: [
    {
      id: 'business_profit_and_loss',
      name: 'Profit and Loss',
      description: 'Income, cost of goods sold, expenses and net profit or loss for the selected period.',
      methodology:
        'Sums posted journal lines of Income, Cost of Goods Sold and Expense accounts dated within the period. Net profit = income − cost of goods sold − expenses. Draft documents are never posted.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['period', 'compare', 'currency'],
      columns: [col('account', 'Account'), money('amount', 'Total')],
      drilldown: 'Account row → Account Transactions',
      comparable: true,
    },
    {
      id: 'business_cash_flow_statement',
      name: 'Cash Flow Statement',
      description: 'Cash inflows and outflows for the period, grouped into operating, investing and financing activities.',
      methodology:
        'Takes every posted journal that touches a cash or bank account, and classifies the cash movement by the account type on the other side: fixed assets → investing; equity and long-term liabilities → financing; everything else → operating. Transfers between cash accounts net to zero.',
      sources: ['ledger', 'banking'],
      permissions: ['ledger:read'],
      filters: ['period', 'bankAccountId'],
      columns: [col('line', 'Activity'), money('amount', 'Amount')],
      drilldown: 'Line → Account Transactions for the cash accounts',
    },
    {
      id: 'business_balance_sheet',
      name: 'Balance Sheet',
      description: 'Assets, liabilities and equity as of a selected date.',
      methodology:
        'Closing balances of Asset, Liability and Equity accounts as of the date, plus current-year net profit shown inside equity so that assets = liabilities + equity.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['asOf', 'compare'],
      columns: [col('account', 'Account'), money('amount', 'Total')],
      drilldown: 'Account row → Account Transactions',
      comparable: true,
    },
    {
      id: 'business_performance_ratio',
      name: 'Business Performance Ratio',
      description: 'Financial ratios derived from actual accounting balances.',
      methodology:
        'Computed from ledger balances as of the end of the period: current ratio, quick ratio, gross profit margin, net profit margin, expense ratio, debt-to-equity and receivables turnover.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['period'],
      columns: [col('ratio', 'Ratio'), num('value', 'Value'), col('formula', 'Formula')],
      drilldown: 'None (derived figures)',
    },
    {
      id: 'business_movement_of_equity',
      name: 'Movement of Equity',
      description: 'Opening equity, changes during the period, and closing equity.',
      methodology:
        'Opening equity = equity accounts plus prior-period profit at the start date; movements = owner contributions/drawings and period net profit; closing equity as of the end date.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['period'],
      columns: [col('line', 'Item'), money('amount', 'Amount')],
      drilldown: 'Equity account → Account Transactions',
    },
    {
      id: 'business_cash_flow_forecast',
      name: 'Cash Flow Forecast',
      description: 'Expected cash inflows and outflows by period from open documents, with explicit assumptions.',
      methodology:
        'Starts from the current balance of cash and bank accounts. Inflows = outstanding invoice balances placed at their due date plus the configured collection delay; outflows = expenses awaiting approval and open purchase-order commitments at their expected date. No assumption is applied unless supplied by the user.',
      sources: ['invoice', 'gstInvoice', 'expense', 'purchaseOrder', 'banking'],
      permissions: ['invoice:read', 'expense:read', 'purchaseOrder:read', 'bankAccount:read'],
      filters: ['period', 'groupBy', 'currency'],
      columns: [col('period', 'Period'), money('inflow', 'Expected inflows'), money('outflow', 'Expected outflows'), money('net', 'Net'), money('balance', 'Projected balance')],
      drilldown: 'Period → open documents falling in it',
    },
  ],
  sales: [
    {
      id: 'sales_by_customer',
      name: 'Sales by Customer',
      description: 'Invoice count, sales excluding tax and sales including tax by customer.',
      methodology:
        'Groups issued invoices (not draft or cancelled) dated in the period by customer account; credit notes issued in the period reduce the totals. Normal and GST invoices are kept separate unless "All" is chosen.',
      sources: ['invoice', 'gstInvoice', 'creditNote'],
      permissions: INV,
      filters: ['period', 'customerId', 'invoiceType', 'currency', 'compare'],
      columns: [col('customer', 'Customer'), num('invoiceCount', 'Invoice count'), money('salesExTax', 'Sales (excl. tax)'), money('salesInclTax', 'Sales (incl. tax)')],
      drilldown: 'Customer → Invoice Details',
      comparable: true,
    },
    {
      id: 'sales_by_item',
      name: 'Sales by Item',
      description: 'Quantity sold, amount and average price per item.',
      methodology:
        'Groups issued invoice lines by item description using each line’s saved snapshot (never the current Item List price). Average price = amount ÷ quantity.',
      sources: ['invoice', 'gstInvoice', 'itemList'],
      permissions: INV,
      filters: ['period', 'itemName', 'itemListId', 'customerId', 'invoiceType', 'currency', 'compare'],
      columns: [col('item', 'Item'), num('quantity', 'Quantity sold'), money('amount', 'Amount'), money('averagePrice', 'Average price')],
      drilldown: 'Item → Invoice Details for that item',
      comparable: true,
    },
    {
      id: 'sales_by_sales_person',
      name: 'Sales by Sales Person',
      description: 'Sales totals and transaction counts grouped by sales representative.',
      methodology:
        'Groups issued invoices by the invoice’s business manager. Invoices with no business manager appear as "Unassigned".',
      sources: ['invoice', 'user'],
      permissions: INV,
      filters: ['period', 'salesPersonId', 'currency'],
      columns: [col('salesPerson', 'Sales person'), num('invoiceCount', 'Invoice count'), money('salesExTax', 'Sales (excl. tax)'), money('salesInclTax', 'Sales (incl. tax)')],
      drilldown: 'Sales person → Invoice Details',
      comparable: true,
    },
    {
      id: 'sales_summary',
      name: 'Sales Summary',
      description: 'Sales totals, invoice counts, tax and credit-note adjustments by day, week or month.',
      methodology:
        'Buckets issued invoices by invoice date; credit notes issued in the same bucket are shown as an adjustment so net sales = invoiced − credit notes.',
      sources: ['invoice', 'gstInvoice', 'creditNote'],
      permissions: INV,
      filters: ['period', 'groupBy', 'invoiceType', 'currency', 'compare'],
      columns: [col('period', 'Period'), num('invoiceCount', 'Invoices'), money('salesExTax', 'Sales (excl. tax)'), money('tax', 'Tax'), money('creditNotes', 'Credit notes'), money('net', 'Net sales')],
      drilldown: 'Period → Invoice Details',
      comparable: true,
    },
  ],
  receivables: [
    {
      id: 'receivables_customer_balance_summary',
      name: 'Customer Balance Summary',
      description: 'Customer-wise invoiced amount, received amount and closing outstanding balance.',
      methodology:
        'Per customer: invoiced − credits applied = billed; received = payments; balance = invoices − payments − credits applied. Unapplied credit-note balances are shown separately and are not netted.',
      sources: ['invoice', 'payment', 'creditNote'],
      permissions: ['invoice:read', 'payment:read'],
      filters: ['asOf', 'customerId', 'invoiceType', 'currency'],
      columns: [col('customer', 'Customer'), money('invoiced', 'Invoiced amount'), money('received', 'Amount received'), money('credits', 'Credits applied'), money('balance', 'Closing balance')],
      drilldown: 'Customer → Receivable Details',
    },
    {
      id: 'receivables_aging_summary',
      name: 'Receivables Aging Summary',
      description: 'Customer balances grouped by overdue-age intervals.',
      methodology:
        'Outstanding invoice balance (after payments and applied credits) placed in an age bucket by days past due as of the chosen date; not-yet-due balances are "Current".',
      sources: ['invoice', 'payment', 'creditNote'],
      permissions: INV,
      filters: ['asOf', 'customerId', 'invoiceType', 'currency', 'ageInterval'],
      columns: [col('customer', 'Customer'), money('current', 'Current'), money('b1', 'Bucket 1'), money('b2', 'Bucket 2'), money('b3', 'Bucket 3'), money('b4', 'Bucket 4'), money('total', 'Total')],
      drilldown: 'Customer → Receivables Aging Details',
    },
    {
      id: 'receivables_aging_details',
      name: 'Receivables Aging Details',
      description: 'Each outstanding invoice and its balance grouped by age.',
      methodology: 'One row per invoice with a balance, in the age bucket for its days past due; subtotal per bucket.',
      sources: ['invoice', 'payment', 'creditNote'],
      permissions: INV,
      filters: ['asOf', 'customerId', 'invoiceType', 'currency', 'ageInterval'],
      columns: [col('bucket', 'Age'), date('date', 'Date'), col('number', 'Invoice#'), col('customer', 'Customer'), status('status', 'Status'), date('dueDate', 'Due date'), num('daysOverdue', 'Days overdue'), money('total', 'Amount'), money('balance', 'Balance due')],
      drilldown: 'Invoice → Invoice page',
      orientation: 'landscape',
    },
    {
      id: 'receivables_invoice_details',
      name: 'Invoice Details',
      description: 'Every invoice with dates, status, amounts, payments and balance.',
      methodology: 'One row per non-draft invoice dated in the period (normal invoices unless GST or All is chosen): total, payments, credits applied and balance due.',
      sources: ['invoice', 'gstInvoice', 'payment', 'creditNote'],
      permissions: ['invoice:read', 'payment:read'],
      filters: ['period', 'customerId', 'invoiceType', 'status', 'paymentStatus', 'currency', 'salesPersonId', 'search'],
      columns: [date('date', 'Date'), col('number', 'Invoice#'), col('type', 'Type'), col('customer', 'Customer'), status('status', 'Status'), date('dueDate', 'Due date'), col('currency', 'Currency'), money('total', 'Amount'), money('paid', 'Paid'), money('credits', 'Credits'), money('balance', 'Balance due')],
      drilldown: 'Invoice → Invoice page',
      orientation: 'landscape',
    },
    {
      id: 'receivables_retainer_invoice_details',
      name: 'Retainer Invoice Details',
      description: 'Retainer invoice balances and unused retainers.',
      methodology: 'Would list retainer invoices with the amount applied and the unused balance.',
      sources: ['retainerInvoice'],
      permissions: INV,
      filters: ['period', 'customerId'],
      columns: [date('date', 'Date'), col('number', 'Retainer invoice#'), col('customer', 'Customer'), money('amount', 'Amount'), money('unused', 'Unused')],
      drilldown: 'Retainer invoice',
      blockedReason: 'WHNUNIVERSE has no retainer-invoice document type.',
    },
    {
      id: 'receivables_sales_order_details',
      name: 'Sales Order Details',
      description: 'Sales order dates, statuses and values.',
      methodology: 'One row per sales order created in the period: status, currency, totals and the quote or invoice it relates to.',
      sources: ['salesOrder'],
      permissions: ['salesOrder:read'],
      filters: ['period', 'customerId', 'status', 'currency', 'search'],
      columns: [date('date', 'Date'), col('number', 'Sales order#'), col('customer', 'Customer'), status('status', 'Status'), date('dueDate', 'Due date'), col('currency', 'Currency'), money('total', 'Amount')],
      drilldown: 'Order → Sales order page',
    },
    {
      id: 'receivables_quote_details',
      name: 'Quote Details',
      description: 'Quote information, values, validity dates and statuses.',
      methodology: 'One row per quote created in the period with status, validity, Item List and total.',
      sources: ['quote', 'itemList'],
      permissions: ['quote:read'],
      filters: ['period', 'customerId', 'status', 'itemListId', 'currency', 'search'],
      columns: [date('date', 'Date'), col('number', 'Quote#'), col('customer', 'Customer'), status('status', 'Status'), date('validUntil', 'Valid until'), col('itemList', 'Item list'), col('currency', 'Currency'), money('total', 'Amount')],
      drilldown: 'Quote → Quote page',
    },
    {
      id: 'receivables_delivery_challan_details',
      name: 'Delivery Challan Details',
      description: 'Delivery challan details.',
      methodology: 'Would list delivery challans with their items and status.',
      sources: ['deliveryChallan'],
      permissions: INV,
      filters: ['period', 'customerId'],
      columns: [date('date', 'Date'), col('number', 'Challan#'), col('customer', 'Customer'), status('status', 'Status')],
      drilldown: 'Challan',
      blockedReason: 'WHNUNIVERSE has no delivery-challan document type.',
    },
    {
      id: 'receivables_receivable_summary',
      name: 'Receivable Summary',
      description: 'Summary of receivable transactions by customer and type.',
      methodology:
        'Per customer: invoices raised, credit notes issued, payments received and credits applied in the period, and the resulting movement in receivables.',
      sources: ['invoice', 'payment', 'creditNote'],
      permissions: ['invoice:read', 'payment:read'],
      filters: ['period', 'customerId', 'invoiceType', 'currency'],
      columns: [col('customer', 'Customer'), money('invoices', 'Invoices'), money('creditNotes', 'Credit notes'), money('payments', 'Payments received'), money('refunds', 'Refunds paid'), money('net', 'Net movement')],
      drilldown: 'Customer → Receivable Details',
    },
    {
      id: 'receivables_receivable_details',
      name: 'Receivable Details',
      description: 'Detailed receivable transactions and the amounts still outstanding.',
      methodology: 'Chronological invoices, credit notes, payments and refunds in the period, with the effect each had on the customer’s receivable.',
      sources: ['invoice', 'payment', 'creditNote'],
      permissions: ['invoice:read', 'payment:read'],
      filters: ['period', 'customerId', 'invoiceType', 'transactionType', 'currency'],
      columns: [date('date', 'Date'), col('customer', 'Customer'), col('type', 'Transaction type'), col('number', 'Number'), money('debit', 'Increases receivable'), money('credit', 'Reduces receivable')],
      drilldown: 'Transaction → its record',
      orientation: 'landscape',
    },
  ],
  payments_received: [
    {
      id: 'payments_received_payments_received',
      name: 'Payments Received',
      description: 'Customer payments with date, reference, method, amount and the invoice each settles.',
      methodology: 'One row per payment received in the period (normal invoices; GST receipts when GST or All is chosen).',
      sources: ['payment', 'gstInvoice', 'banking'],
      permissions: ['payment:read'],
      filters: ['period', 'customerId', 'invoiceType', 'bankAccountId', 'currency', 'search'],
      columns: [date('date', 'Date'), col('number', 'Payment#'), col('reference', 'Reference'), col('customer', 'Customer'), col('invoice', 'Invoice#'), col('method', 'Method'), col('bank', 'Deposited to'), col('currency', 'Currency'), money('amount', 'Amount')],
      drilldown: 'Payment → payment page; invoice → invoice page',
      orientation: 'landscape',
    },
    {
      id: 'payments_received_time_to_get_paid',
      name: 'Time to Get Paid',
      description: 'How many days customers take to pay, in configurable intervals.',
      methodology: 'For each payment, days = payment date − invoice date. Payments are bucketed by days; averages shown per customer.',
      sources: ['payment', 'invoice'],
      permissions: ['payment:read', 'invoice:read'],
      filters: ['period', 'customerId', 'invoiceType', 'currency', 'ageInterval'],
      columns: [col('bucket', 'Days to pay'), num('payments', 'Payments'), money('amount', 'Amount'), num('averageDays', 'Average days')],
      drilldown: 'Bucket → Payments Received',
    },
    {
      id: 'payments_received_credit_note_details',
      name: 'Credit Note Details',
      description: 'Credit notes with amounts, applications and remaining balances.',
      methodology: 'One row per issued credit note: total, applied to invoices, refunded in cash and the unused balance = total − applied − refunded.',
      sources: ['creditNote'],
      permissions: ['creditNote:read'],
      filters: ['period', 'customerId', 'status', 'currency', 'search'],
      columns: [date('date', 'Date'), col('number', 'Credit note#'), col('customer', 'Customer'), col('invoice', 'Invoice#'), status('status', 'Status'), col('currency', 'Currency'), money('total', 'Amount'), money('applied', 'Applied'), money('refunded', 'Refunded'), money('unused', 'Unused')],
      drilldown: 'Credit note → credit note page',
      orientation: 'landscape',
    },
    {
      id: 'payments_received_refund_history',
      name: 'Refund History (Payments Received)',
      description: 'Refunds paid to customers.',
      methodology: 'One row per credit-note refund in the period: date, method, reference, bank account and the credit note refunded. Applying a credit to an invoice is not a refund and does not appear.',
      sources: ['creditNote', 'banking'],
      permissions: ['creditNote:read'],
      filters: ['period', 'customerId', 'bankAccountId', 'currency'],
      columns: [date('date', 'Date'), col('creditNote', 'Credit note#'), col('customer', 'Customer'), col('method', 'Method'), col('reference', 'Reference'), col('bank', 'Paid from'), col('currency', 'Currency'), money('amount', 'Amount')],
      drilldown: 'Credit note → credit note page',
    },
  ],
  inventory: [
    ...(
      [
        ['inventory_inventory_summary', 'Inventory Summary', 'Quantities in, out, ordered, available and on hand for inventory-tracked items.'],
        ['inventory_inventory_valuation_summary', 'Inventory Valuation Summary', 'Inventory quantities and valuation using the configured cost method.'],
        ['inventory_fifo_cost_lot_tracking', 'FIFO Cost Lot Tracking', 'FIFO cost lots with remaining quantities and values.'],
      ] as const
    ).map(([id, name, description]): Spec => ({
      id,
      name,
      description,
      methodology: 'Requires stock movements and cost lots.',
      sources: ['stock'],
      permissions: ['product:read'],
      filters: ['asOf'],
      columns: [col('item', 'Item'), num('quantity', 'Quantity'), money('value', 'Value')],
      drilldown: 'Item → stock movements',
      blockedReason:
        'WHNUNIVERSE has no stock tracking: Item Lists are price lists, and there are no quantities on hand, stock movements or cost lots to report.',
    })),
    {
      id: 'inventory_abc_classification',
      name: 'ABC Classification',
      description: 'Classifies items A, B or C by their contribution to revenue.',
      methodology:
        'Ranks invoiced items by revenue in the period (line snapshots, not current prices). Class A = items making up the first 70% of cumulative revenue, B = the next 20%, C = the remaining 10%. Uses sales data only — no stock figures.',
      sources: ['invoice', 'gstInvoice'],
      permissions: INV,
      filters: ['period', 'invoiceType', 'currency'],
      columns: [col('item', 'Item'), money('revenue', 'Revenue'), num('share', 'Share %'), num('cumulative', 'Cumulative %'), col('class', 'Class')],
      drilldown: 'Item → Sales by Item',
    },
    ...(
      [
        ['inventory_inventory_aging_summary', 'Inventory Aging Summary', 'Stock quantities and values grouped by age intervals.'],
        ['inventory_inventory_adjustment_summary', 'Inventory Adjustment Summary', 'Inventory increases, decreases and value changes by date, reason or type.'],
        ['inventory_inventory_adjustment_details', 'Inventory Adjustment Details', 'Individual inventory adjustments with items, quantities, values and reasons.'],
      ] as const
    ).map(([id, name, description]): Spec => ({
      id,
      name,
      description,
      methodology: 'Requires stock movements and adjustments.',
      sources: ['stock'],
      permissions: ['product:read'],
      filters: ['period'],
      columns: [col('item', 'Item'), num('quantity', 'Quantity'), money('value', 'Value')],
      drilldown: 'Adjustment → record',
      blockedReason:
        'WHNUNIVERSE has no stock tracking: there are no inventory adjustments or stock ages to report.',
    })),
  ],
  payables_purchases: [
    {
      id: 'payables_vendor_balance_summary',
      name: 'Vendor Balance Summary',
      description: 'Vendor billed amounts, payments and closing payable balances.',
      methodology:
        'Expenses linked to a vendor are the vendor’s recorded bills. Billed = recorded expenses not rejected; paid = approved expenses (posted to the bank when a bank account is chosen); payable = recorded but still awaiting approval. Open purchase orders are shown as commitments and are never added to the balance.',
      sources: ['vendor', 'expense', 'purchaseOrder'],
      permissions: ['vendor:read', 'expense:read'],
      filters: ['asOf', 'vendorId', 'currency'],
      columns: [col('vendor', 'Vendor'), money('billed', 'Billed'), money('paid', 'Paid'), money('payable', 'Payable'), money('commitments', 'Open PO commitments')],
      drilldown: 'Vendor → Payable Details',
    },
    {
      id: 'payables_aging_summary',
      name: 'Payables Aging Summary',
      description: 'Outstanding vendor payables grouped by age.',
      methodology: 'Recorded expenses awaiting approval, aged from their due date (expense date + the vendor’s payment terms) as of the chosen date.',
      sources: ['vendor', 'expense'],
      permissions: ['vendor:read', 'expense:read'],
      filters: ['asOf', 'vendorId', 'currency', 'ageInterval'],
      columns: [col('vendor', 'Vendor'), money('current', 'Current'), money('b1', 'Bucket 1'), money('b2', 'Bucket 2'), money('b3', 'Bucket 3'), money('b4', 'Bucket 4'), money('total', 'Total')],
      drilldown: 'Vendor → Payables Aging Details',
    },
    {
      id: 'payables_aging_details',
      name: 'Payables Aging Details',
      description: 'Individual payables grouped by age.',
      methodology: 'One row per recorded expense awaiting approval, placed in an age bucket by days past its due date.',
      sources: ['vendor', 'expense'],
      permissions: ['vendor:read', 'expense:read'],
      filters: ['asOf', 'vendorId', 'currency', 'ageInterval'],
      columns: [col('bucket', 'Age'), date('date', 'Date'), col('name', 'Expense'), col('vendor', 'Vendor'), date('dueDate', 'Due date'), num('daysOverdue', 'Days overdue'), money('amount', 'Amount')],
      drilldown: 'Expense → expense page',
    },
    {
      id: 'payables_bills_details',
      name: 'Bills Details',
      description: 'Vendor bills with dates, amounts, statuses and outstanding balances.',
      methodology: 'Would list vendor bills.',
      sources: ['bill'],
      permissions: ['vendor:read'],
      filters: ['period', 'vendorId'],
      columns: [date('date', 'Date'), col('number', 'Bill#'), col('vendor', 'Vendor'), money('amount', 'Amount'), money('balance', 'Balance')],
      drilldown: 'Bill',
      blockedReason: 'WHNUNIVERSE has no vendor-bill document. Vendor spend is recorded as Expenses; see Payable Details.',
    },
    {
      id: 'payables_recurring_bills_details',
      name: 'Recurring Bills Details',
      description: 'Recurring vendor bill profiles and the bills they generated.',
      methodology: 'Would list recurring bill profiles.',
      sources: ['recurringBill'],
      permissions: ['vendor:read'],
      filters: ['period', 'vendorId'],
      columns: [col('name', 'Profile'), col('vendor', 'Vendor'), col('frequency', 'Frequency'), date('next', 'Next bill')],
      drilldown: 'Profile',
      blockedReason: 'WHNUNIVERSE has no recurring vendor bills. Recurring spend is a Recurring Expense; see Recurring Expense Details.',
    },
    {
      id: 'payables_vendor_credits_details',
      name: 'Vendor Credits Details',
      description: 'Vendor credits with applied and unused balances.',
      methodology: 'Would list vendor credits.',
      sources: ['vendorCredit'],
      permissions: ['vendor:read'],
      filters: ['period', 'vendorId'],
      columns: [date('date', 'Date'), col('number', 'Credit#'), col('vendor', 'Vendor'), money('amount', 'Amount'), money('unused', 'Unused')],
      drilldown: 'Vendor credit',
      blockedReason: 'WHNUNIVERSE has no vendor credits.',
    },
    {
      id: 'payables_payments_made',
      name: 'Payments Made',
      description: 'Outgoing vendor payments with method, amount and bank account.',
      methodology: 'Approved expenses linked to a vendor, dated in the period, with the bank account and payment method they were paid through.',
      sources: ['expense', 'vendor', 'banking'],
      permissions: ['expense:read', 'vendor:read'],
      filters: ['period', 'vendorId', 'bankAccountId', 'currency', 'search'],
      columns: [date('date', 'Date'), col('name', 'Expense'), col('vendor', 'Vendor'), col('method', 'Method'), col('bank', 'Paid from'), col('purchaseOrder', 'Purchase order'), col('currency', 'Currency'), money('amount', 'Amount')],
      drilldown: 'Expense → expense page',
    },
    {
      id: 'payables_refund_history',
      name: 'Refund History (Payments Made)',
      description: 'Refunds received from vendors.',
      methodology: 'Would list vendor refunds against payments made.',
      sources: ['vendorRefund'],
      permissions: ['vendor:read'],
      filters: ['period', 'vendorId'],
      columns: [date('date', 'Date'), col('vendor', 'Vendor'), money('amount', 'Amount')],
      drilldown: 'Refund',
      blockedReason: 'WHNUNIVERSE does not record refunds received from vendors.',
    },
    {
      id: 'payables_purchase_order_details',
      name: 'Purchase Order Details',
      description: 'Purchase-order dates, vendors, statuses, delivery dates and totals.',
      methodology: 'One row per non-deleted purchase order dated in the period. A purchase order is a commitment: the linked expense, if any, is shown separately.',
      sources: ['purchaseOrder', 'vendor', 'expense'],
      permissions: ['purchaseOrder:read'],
      filters: ['period', 'vendorId', 'status', 'currency', 'search'],
      columns: [date('date', 'Date'), col('number', 'PO#'), col('vendor', 'Vendor'), col('subject', 'Subject'), status('status', 'Status'), date('expected', 'Expected by'), col('currency', 'Currency'), money('total', 'Amount'), col('expense', 'Expense')],
      drilldown: 'PO → purchase order page',
      orientation: 'landscape',
    },
    {
      id: 'payables_purchase_orders_by_vendor',
      name: 'Purchase Orders by Vendor',
      description: 'Purchase-order count and value by vendor.',
      methodology: 'Groups non-cancelled purchase orders by vendor: count, total, and how much has already been turned into an expense.',
      sources: ['purchaseOrder', 'expense'],
      permissions: ['purchaseOrder:read'],
      filters: ['period', 'vendorId', 'status', 'currency'],
      columns: [col('vendor', 'Vendor'), num('count', 'Orders'), money('total', 'Order value'), money('expensed', 'Billed as expense'), money('open', 'Still open')],
      drilldown: 'Vendor → Purchase Order Details',
    },
    {
      id: 'payables_purchase_order_by_item',
      name: 'Purchase Order by Item',
      description: 'Ordered quantities and value by item or line description.',
      methodology: 'Groups purchase-order lines (free text) by description: ordered quantity and line value.',
      sources: ['purchaseOrder'],
      permissions: ['purchaseOrder:read'],
      filters: ['period', 'vendorId', 'itemName', 'status', 'currency'],
      columns: [col('item', 'Item'), num('quantity', 'Quantity ordered'), money('amount', 'Amount'), num('orders', 'Orders')],
      drilldown: 'Item → Purchase Order Details',
    },
    {
      id: 'payables_purchases_by_vendor',
      name: 'Purchases by Vendor',
      description: 'Purchases and related spend grouped by vendor.',
      methodology: 'Groups approved expenses by vendor (the linked vendor record, or the free-text vendor name).',
      sources: ['expense', 'vendor'],
      permissions: ['expense:read'],
      filters: ['period', 'vendorId', 'expenseCategory', 'currency', 'compare'],
      columns: [col('vendor', 'Vendor'), num('count', 'Expenses'), money('amount', 'Amount')],
      drilldown: 'Vendor → Expense Details',
      comparable: true,
    },
    {
      id: 'payables_purchases_by_item',
      name: 'Purchases by Item',
      description: 'Purchases and costs grouped by item.',
      methodology: 'Groups approved expenses by expense name, which is the item or service bought.',
      sources: ['expense'],
      permissions: ['expense:read'],
      filters: ['period', 'vendorId', 'itemName', 'expenseCategory', 'currency'],
      columns: [col('item', 'Item'), num('count', 'Expenses'), money('amount', 'Amount')],
      drilldown: 'Item → Expense Details',
    },
    {
      id: 'payables_payable_summary',
      name: 'Payable Summary',
      description: 'Vendor payables with transaction status and outstanding balance.',
      methodology: 'Per vendor: recorded expenses by approval status (awaiting approval, approved, rejected) and the payable balance.',
      sources: ['expense', 'vendor'],
      permissions: ['expense:read', 'vendor:read'],
      filters: ['period', 'vendorId', 'currency'],
      columns: [col('vendor', 'Vendor'), money('pending', 'Awaiting approval'), money('approved', 'Approved (paid)'), money('rejected', 'Rejected'), money('payable', 'Payable')],
      drilldown: 'Vendor → Payable Details',
    },
    {
      id: 'payables_payable_details',
      name: 'Payable Details',
      description: 'Detailed vendor transactions with their line information.',
      methodology: 'One row per expense or purchase order for a vendor in the period, with status, amount and the document it came from.',
      sources: ['expense', 'purchaseOrder', 'vendor'],
      permissions: ['expense:read', 'purchaseOrder:read'],
      filters: ['period', 'vendorId', 'status', 'currency', 'transactionType'],
      columns: [date('date', 'Date'), col('vendor', 'Vendor'), col('type', 'Type'), col('number', 'Reference'), status('status', 'Status'), col('currency', 'Currency'), money('amount', 'Amount')],
      drilldown: 'Row → its record',
      orientation: 'landscape',
    },
    ...(
      [
        ['payables_purchase_return_summary', 'Purchase Return Summary', 'Summary of vendor purchase returns.'],
        ['payables_purchase_return_details', 'Purchase Return Details', 'Detailed purchase-return records.'],
      ] as const
    ).map(([id, name, description]): Spec => ({
      id,
      name,
      description,
      methodology: 'Would list vendor purchase returns.',
      sources: ['purchaseReturn'],
      permissions: ['vendor:read'],
      filters: ['period', 'vendorId'],
      columns: [date('date', 'Date'), col('vendor', 'Vendor'), money('amount', 'Amount')],
      drilldown: 'Purchase return',
      blockedReason: 'WHNUNIVERSE does not record purchase returns.',
    })),
  ],
  purchases_expenses: [
    {
      id: 'expenses_expense_details',
      name: 'Expense Details',
      description: 'Detailed expense records with dates, categories, vendors, amounts and payment sources.',
      methodology: 'One row per expense dated in the period, with approval status and the purchase order it was created from. Only approved expenses are posted to the ledger.',
      sources: ['expense'],
      permissions: ['expense:read'],
      filters: ['period', 'expenseCategory', 'vendorId', 'customerId', 'bankAccountId', 'status', 'currency', 'search'],
      columns: [date('date', 'Date'), col('name', 'Expense'), col('category', 'Category'), col('vendor', 'Vendor'), col('customer', 'Customer'), col('method', 'Method'), col('bank', 'Paid from'), status('status', 'Approval'), col('currency', 'Currency'), money('amount', 'Amount')],
      drilldown: 'Expense → expense page',
      orientation: 'landscape',
    },
    {
      id: 'expenses_expenses_by_category',
      name: 'Expenses by Category',
      description: 'Expense totals and counts by category.',
      methodology: 'Groups approved expenses by category ("Uncategorised" when blank).',
      sources: ['expense'],
      permissions: ['expense:read'],
      filters: ['period', 'expenseCategory', 'vendorId', 'currency', 'compare'],
      columns: [col('category', 'Category'), num('count', 'Expenses'), money('amount', 'Amount')],
      drilldown: 'Category → Expense Details',
      comparable: true,
    },
    {
      id: 'expenses_expenses_by_customer',
      name: 'Expenses by Customer',
      description: 'Expenses linked to a customer account.',
      methodology: 'Groups approved expenses by the customer account they are linked to. Expense reports tied to a customer invoice are shown in their own columns without being added to this total.',
      sources: ['expense', 'expenseReport'],
      permissions: ['expense:read'],
      filters: ['period', 'customerId', 'expenseCategory', 'currency'],
      columns: [col('customer', 'Customer'), num('count', 'Expenses'), money('amount', 'Amount')],
      drilldown: 'Customer → Expense Details',
    },
    {
      id: 'expenses_expenses_by_project',
      name: 'Expenses by Project',
      description: 'Expense totals by project.',
      methodology: 'Would group expenses by project.',
      sources: ['project'],
      permissions: ['expense:read'],
      filters: ['period'],
      columns: [col('project', 'Project'), money('amount', 'Amount')],
      drilldown: 'Project',
      blockedReason: 'WHNUNIVERSE has no projects module.',
    },
    {
      id: 'expenses_expenses_by_employee',
      name: 'Expenses by Employee',
      description: 'Expense totals by the person who incurred them.',
      methodology:
        'Approved expenses grouped by their business manager, and expense reports grouped by claimant with each workflow stage counted separately: submitted, approved, cash given, awaiting verification and reimbursed.',
      sources: ['expense', 'expenseReport', 'user'],
      permissions: ['expense:read', 'expenseReport:read'],
      filters: ['period', 'claimantId', 'expenseCategory', 'currency'],
      columns: [col('employee', 'Employee'), num('expenses', 'Expenses'), money('expenseAmount', 'Expense amount'), money('reported', 'Claimed in reports'), money('reimbursed', 'Reimbursed')],
      drilldown: 'Employee → Expense Details',
    },
    {
      id: 'expenses_billable_expense_details',
      name: 'Billable Expense Details',
      description: 'Expenses marked billable and their billing records.',
      methodology: 'Would list billable expenses.',
      sources: ['expense'],
      permissions: ['expense:read'],
      filters: ['period', 'customerId'],
      columns: [date('date', 'Date'), col('name', 'Expense'), col('customer', 'Customer'), money('amount', 'Amount')],
      drilldown: 'Expense',
      blockedReason: 'WHNUNIVERSE expenses have no billable flag or customer-billing link.',
    },
    {
      id: 'expenses_recurring_expense_details',
      name: 'Recurring Expense Details',
      description: 'Recurring expense schedules, next occurrence, status and generated expenses.',
      methodology: 'One row per recurring expense profile with frequency, next run, status, and the count and total of expenses it has generated.',
      sources: ['recurringExpense', 'expense'],
      permissions: ['recurringExpense:read', 'expense:read'],
      filters: ['status', 'vendorId', 'expenseCategory', 'currency'],
      columns: [col('name', 'Profile'), col('vendor', 'Vendor'), col('category', 'Category'), col('frequency', 'Frequency'), date('next', 'Next run'), status('status', 'Status'), num('generated', 'Generated'), money('generatedAmount', 'Generated amount'), money('amount', 'Amount each')],
      drilldown: 'Profile → recurring expense page',
    },
    {
      id: 'expenses_mileage_expenses_by_employee',
      name: 'Mileage Expenses by Employee',
      description: 'Mileage expense totals by employee.',
      methodology: 'Would group mileage expenses by employee.',
      sources: ['mileage'],
      permissions: ['expense:read'],
      filters: ['period', 'claimantId'],
      columns: [col('employee', 'Employee'), num('distance', 'Distance'), money('amount', 'Amount')],
      drilldown: 'Employee',
      blockedReason: 'WHNUNIVERSE does not record mileage expenses.',
    },
  ],
  accountant: [
    {
      id: 'accountant_account_transactions',
      name: 'Account Transactions',
      description: 'Account-level transaction history with debits, credits and running balance.',
      methodology: 'Posted journal lines for the chosen account(s) in the period, with the opening balance and a running balance.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['period', 'ledgerAccountId', 'accountType', 'transactionType', 'search'],
      columns: [date('date', 'Date'), col('account', 'Account'), col('description', 'Transaction details'), col('type', 'Transaction type'), col('number', 'Transaction#'), money('debit', 'Debit'), money('credit', 'Credit'), money('balance', 'Balance')],
      drilldown: 'Row → source record',
      orientation: 'landscape',
    },
    {
      id: 'accountant_account_type_summary',
      name: 'Account Type Summary',
      description: 'Totals grouped by Chart of Accounts account type.',
      methodology: 'Opening balance, debits, credits and closing balance summed per account type.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['period', 'accountType'],
      columns: [col('type', 'Account type'), money('opening', 'Opening'), money('debit', 'Debit'), money('credit', 'Credit'), money('closing', 'Closing')],
      drilldown: 'Type → General Ledger',
    },
    {
      id: 'accountant_general_ledger',
      name: 'General Ledger',
      description: 'Ledger activity and balances by account over the period.',
      methodology: 'Per account: opening balance, total debits, total credits and closing balance.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['period', 'ledgerAccountId', 'accountType'],
      columns: [col('account', 'Account'), col('type', 'Type'), money('opening', 'Opening'), money('debit', 'Debit'), money('credit', 'Credit'), money('closing', 'Closing')],
      drilldown: 'Account → Detailed General Ledger',
    },
    {
      id: 'accountant_detailed_general_ledger',
      name: 'Detailed General Ledger',
      description: 'Transaction-level ledger with opening balance, debits, credits and closing balance per account.',
      methodology: 'Every posted journal line per account, grouped by account with an opening-balance line first and a closing-balance line last.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['period', 'ledgerAccountId', 'accountType'],
      columns: [date('date', 'Date'), col('account', 'Account'), col('description', 'Details'), col('type', 'Type'), col('number', 'Number'), money('debit', 'Debit'), money('credit', 'Credit'), money('balance', 'Balance')],
      drilldown: 'Row → source record',
      orientation: 'landscape',
    },
    {
      id: 'accountant_trial_balance',
      name: 'Trial Balance',
      description: 'Closing debit and credit balances by account as at the end of the period.',
      methodology: 'Every account’s balance from the beginning of the books to the end of the period, placed on its debit or credit side. Total debits equal total credits by construction; the report states if they ever do not. For opening, movement and closing columns use the General Ledger.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['period', 'accountType', 'includeAdjustmentPeriod'],
      columns: [col('account', 'Account'), col('type', 'Type'), money('debit', 'Debit'), money('credit', 'Credit')],
      drilldown: 'Account → Account Transactions',
    },
    {
      id: 'accountant_journals',
      name: 'Journals',
      description: 'Manual and system journal entries with their postings.',
      methodology: 'Every journal dated in the period with its lines; system journals show the document that produced them.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['period', 'transactionType', 'ledgerAccountId', 'search'],
      columns: [date('date', 'Date'), col('number', 'Journal#'), col('source', 'Source'), col('account', 'Account'), col('description', 'Description'), money('debit', 'Debit'), money('credit', 'Credit')],
      drilldown: 'Journal → source record',
      orientation: 'landscape',
    },
    {
      id: 'accountant_daybook',
      name: 'Daybook',
      description: 'Chronological accounting transactions in the period.',
      methodology: 'Journals in the period ordered by date then posting time, one line per journal with total and source.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['period', 'transactionType', 'search'],
      columns: [date('date', 'Date'), col('number', 'Journal#'), col('source', 'Type'), col('description', 'Description'), money('amount', 'Amount')],
      drilldown: 'Journal → source record',
    },
    {
      id: 'accountant_budgets_vs_actuals',
      name: 'Budgets Vs Actuals',
      description: 'Actual results against configured budgets.',
      methodology: 'For each budgeted account: budget for the months in the period, actual posted movement, variance and variance %. Accounts with no budget are omitted.',
      sources: ['ledger', 'budget'],
      permissions: ['ledger:read'],
      filters: ['period', 'accountType'],
      columns: [col('account', 'Account'), money('budget', 'Budget'), money('actual', 'Actual'), money('variance', 'Variance'), num('variancePct', 'Variance %')],
      drilldown: 'Account → Account Transactions',
    },
    {
      id: 'accountant_13th_month_trial_balance',
      name: '13th Month Trial Balance',
      description: 'Trial balance including year-end adjustments posted in the 13th period.',
      methodology: 'Trial balance for the fiscal year with columns before adjustments, the 13th-period adjustments, and after adjustments. Adjustments are journals flagged as year-end adjustment entries.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['period', 'accountType'],
      columns: [col('account', 'Account'), money('before', 'Before adjustments'), money('adjustments', '13th-period adjustments'), money('after', 'After adjustments')],
      drilldown: 'Account → Account Transactions',
    },
    {
      id: 'accountant_horizontal_profit_and_loss',
      name: 'Horizontal Profit and Loss',
      description: 'Profit and loss by account, month by month, across the period.',
      methodology: 'Income, cost of goods sold and expense accounts with one column per month in the period and a total.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['period'],
      columns: [col('account', 'Account'), money('total', 'Total')],
      drilldown: 'Account → Account Transactions',
      orientation: 'landscape',
    },
  ],
  projects_timesheets: [
    ...(
      [
        ['projects_project_summary', 'Project Summary', 'Project status, budget, costs, revenue and related figures.'],
        ['projects_project_revenue_summary', 'Project Revenue Summary', 'Budget and actual revenue by project.'],
        ['projects_timesheet_details', 'Timesheet Details', 'Logged, billable, non-billable, billed and unbilled hours.'],
      ] as const
    ).map(([id, name, description]): Spec => ({
      id,
      name,
      description,
      methodology: 'Requires projects and timesheets.',
      sources: ['project', 'timesheet'],
      permissions: ['invoice:read'],
      filters: ['period'],
      columns: [col('project', 'Project'), num('hours', 'Hours'), money('amount', 'Amount')],
      drilldown: 'Project',
      blockedReason: 'WHNUNIVERSE has no projects or timesheets module, and none is invented to fill the report.',
    })),
  ],
  recurring_transactions: [
    {
      id: 'recurring_recurring_invoice_details',
      name: 'Recurring Invoice Details',
      description: 'Recurring invoice profiles, frequency, status and generated invoices.',
      methodology: 'Would list recurring invoice profiles.',
      sources: ['recurringInvoice'],
      permissions: INV,
      filters: ['period', 'customerId'],
      columns: [col('profile', 'Profile'), col('customer', 'Customer'), col('frequency', 'Frequency'), date('next', 'Next invoice')],
      drilldown: 'Profile',
      blockedReason: 'WHNUNIVERSE has no recurring invoices (only recurring expenses).',
    },
  ],
  taxes: [
    {
      id: 'taxes_tax_summary',
      name: 'Tax Summary',
      description: 'Taxable amounts and tax totals by rate and transaction type.',
      methodology:
        'Tax collected on issued invoice lines (by line tax rate) and on GST invoices (18% of the entered price), less tax on credit notes issued in the period. Reports tax recorded in WHNUNIVERSE; it is not a return.',
      sources: ['invoice', 'gstInvoice', 'creditNote'],
      permissions: INV,
      filters: ['period', 'invoiceType', 'taxRate', 'currency'],
      columns: [col('type', 'Transaction type'), num('rate', 'Tax rate %'), money('taxable', 'Taxable amount'), money('tax', 'Tax amount')],
      drilldown: 'Row → Invoice Details',
    },
    ...(
      [
        ['taxes_tds_receivables_summary', 'TDS Receivables Summary', 'TDS deducted by customers, by section.', 'WHNUNIVERSE does not record TDS deducted by customers (no TDS section or deduction data).'],
        ['taxes_tds_payables_summary', 'TDS Payables Summary', 'TDS withheld from vendor payments and the amount due for remittance.', 'WHNUNIVERSE does not record TDS withholding on vendor payments.'],
        ['taxes_tcs_summary', 'TCS Summary (Form No. 27EQ)', 'Tax Collected at Source transactions and totals.', 'WHNUNIVERSE does not record TCS.'],
      ] as const
    ).map(([id, name, description, blockedReason]): Spec => ({
      id,
      name,
      description,
      methodology: 'Requires withholding or collection data per transaction.',
      sources: ['tax'],
      permissions: INV,
      filters: ['period'],
      columns: [col('section', 'Section'), money('amount', 'Amount')],
      drilldown: 'Transaction',
      blockedReason,
      regions: ['IN'],
    })),
    {
      id: 'taxes_overseas_tax_summary',
      name: 'Overseas Tax Summary Report',
      description: 'Tax summary for overseas transactions.',
      methodology: 'Applies to businesses registered under overseas VAT schemes.',
      sources: ['tax'],
      permissions: INV,
      filters: ['period'],
      columns: [col('type', 'Type'), money('tax', 'Tax')],
      drilldown: 'Transaction',
      regions: ['GCC', 'OVERSEAS'],
      regionNote: 'Applies to overseas VAT jurisdictions; this organisation is configured for India (GST).',
    },
    {
      id: 'taxes_vat_moss_report',
      name: 'VAT MOSS Report',
      description: 'EU VAT Mini One Stop Shop report.',
      methodology: 'Applies to EU digital-service sellers.',
      sources: ['tax'],
      permissions: INV,
      filters: ['period'],
      columns: [col('country', 'Country'), money('tax', 'VAT')],
      drilldown: 'Transaction',
      regions: ['EU'],
      regionNote: 'Applies to EU VAT jurisdictions; this organisation is configured for India (GST).',
    },
    {
      id: 'taxes_oss_ioss_scheme_report',
      name: 'OSS/IOSS Scheme Report',
      description: 'EU One Stop Shop / Import One Stop Shop scheme report.',
      methodology: 'Applies to EU distance sellers.',
      sources: ['tax'],
      permissions: INV,
      filters: ['period'],
      columns: [col('country', 'Country'), money('tax', 'VAT')],
      drilldown: 'Transaction',
      regions: ['EU'],
      regionNote: 'Applies to EU VAT jurisdictions; this organisation is configured for India (GST).',
    },
    {
      id: 'taxes_fec_report',
      name: 'FEC Report',
      description: 'French Fichier des Écritures Comptables export.',
      methodology: 'Applies to French accounting-record requirements.',
      sources: ['ledger'],
      permissions: ['ledger:read'],
      filters: ['period'],
      columns: [col('journal', 'JournalCode'), date('date', 'EcritureDate')],
      drilldown: 'Journal',
      regions: ['FR'],
      regionNote: 'Applies to French jurisdictions; this organisation is configured for India (GST).',
    },
    ...(
      [
        ['taxes_reverse_charge_summary', 'Reverse Charge Summary', 'Purchases subject to reverse charge and the tax payable or claimable.'],
        ['taxes_sales_reverse_charge_summary', 'Sales Reverse Charge Summary', 'Sales subject to reverse-charge rules.'],
      ] as const
    ).map(([id, name, description]): Spec => ({
      id,
      name,
      description,
      methodology: 'Requires a reverse-charge flag and tax amount per transaction.',
      sources: ['tax'],
      permissions: INV,
      filters: ['period'],
      columns: [col('type', 'Type'), money('taxable', 'Taxable'), money('tax', 'Tax')],
      drilldown: 'Transaction',
      blockedReason: 'WHNUNIVERSE transactions carry no reverse-charge treatment.',
    })),
  ],
  activity: [
    {
      id: 'activity_activity_logs',
      name: 'Activity Logs',
      description: 'Audit history of creations, updates, deletions, approvals, refunds, payments, bank transactions, exports and configuration changes.',
      methodology: 'Reads the append-only audit log, filtered by date, record type, action and user.',
      sources: ['auditLog'],
      permissions: ['auditLog:read'],
      filters: ['period', 'entityType', 'actorId', 'transactionType', 'search'],
      columns: [col('at', 'Date and time'), col('actor', 'User'), col('action', 'Action'), col('entityType', 'Record type'), col('label', 'Record'), col('ip', 'IP address')],
      drilldown: 'Entity → record when it still exists',
      orientation: 'landscape',
    },
  ],
};

/** WHNUNIVERSE-specific reports. Positioned after the standard categories and never part of the 81. */
const WHN_SPECS: Spec[] = [
  {
    id: 'whn_gst_invoice_details',
    name: 'GST Invoice Details',
    description: 'GST invoices with their own numbering, designated bank account, payments, credits and balance.',
    methodology: 'One row per issued GST invoice: taxable value, GST, total, paid and balance. Kept apart from normal invoices.',
    sources: ['gstInvoice', 'banking'],
    permissions: ['gstInvoice:read'],
    filters: ['period', 'customerId', 'status', 'bankAccountId', 'currency', 'search'],
    columns: [date('date', 'Date'), col('number', 'GST invoice#'), col('customer', 'Customer'), status('status', 'Status'), col('bank', 'Bank account'), col('currency', 'Currency'), money('taxable', 'Taxable'), money('gst', 'GST'), money('total', 'Total'), money('paid', 'Paid'), money('balance', 'Balance')],
    drilldown: 'Invoice → GST invoice page',
    orientation: 'landscape',
  },
  {
    id: 'whn_expense_report_details',
    name: 'Expense Report Details',
    description: 'Expense reports with approval, cash disbursement and reimbursement verification.',
    methodology:
      'One row per report: status, approver, cash officer, cash given, who verified, and the reimbursement date. A report counts as reimbursed only once the accountant has verified it.',
    sources: ['expenseReport', 'invoice'],
    permissions: ['expenseReport:read'],
    filters: ['period', 'claimantId', 'expenseReportStatus', 'approverId', 'cashOfficerId', 'reimbursementStatus', 'currency', 'search'],
    columns: [date('date', 'Submitted'), col('number', 'Report#'), col('title', 'Title'), col('claimant', 'Claimant'), status('status', 'Status'), col('approver', 'Approved by'), col('cashOfficer', 'Cash given by'), money('disbursed', 'Cash given'), col('verifier', 'Verified by'), date('reimbursedOn', 'Reimbursed on'), col('invoice', 'Linked invoice'), col('currency', 'Currency'), money('total', 'Total')],
    drilldown: 'Report → expense report page',
    orientation: 'landscape',
  },
  {
    id: 'whn_expense_report_summary',
    name: 'Expense Report Summary',
    description: 'Expense report totals by workflow stage, category or claimant.',
    methodology: 'Counts and totals of reports in each workflow state (draft, pending approval, awaiting cash, cash given, pending verification, reimbursed, rejected, cancelled).',
    sources: ['expenseReport'],
    permissions: ['expenseReport:read'],
    filters: ['period', 'claimantId', 'groupBy', 'currency'],
    columns: [col('group', 'Group'), num('count', 'Reports'), money('total', 'Claimed'), money('disbursed', 'Cash given')],
    drilldown: 'Group → Expense Report Details',
  },
  {
    id: 'whn_credit_note_reconciliation',
    name: 'Credit Note Reconciliation',
    description: 'Credit notes issued, applied to invoices, refunded in cash and still unused.',
    methodology: 'Issued = total of issued credit notes; applied and refunded are separate ledgers; unused = issued − applied − refunded. A credit applied to an invoice is never treated as a cash refund.',
    sources: ['creditNote'],
    permissions: ['creditNote:read'],
    filters: ['period', 'customerId', 'currency'],
    columns: [col('customer', 'Customer'), money('issued', 'Issued'), money('applied', 'Applied to invoices'), money('refunded', 'Refunded in cash'), money('unused', 'Unused')],
    drilldown: 'Customer → Credit Note Details',
  },
  {
    id: 'whn_bank_account_summary',
    name: 'Bank Account Summary',
    description: 'Opening balance, receipts, payments, transfers and closing balance per bank account.',
    methodology: 'Per bank account from its transaction ledger: opening, money in, money out, transfers, adjustments and closing balance. Each account stays in its own currency.',
    sources: ['banking'],
    permissions: ['bankTransaction:read'],
    filters: ['period', 'bankAccountId'],
    columns: [col('account', 'Account'), col('currency', 'Currency'), money('opening', 'Opening'), money('in', 'Money in'), money('out', 'Money out'), money('closing', 'Closing')],
    drilldown: 'Account → bank account page',
  },
  {
    id: 'whn_vendor_commitments',
    name: 'Vendor Commitments',
    description: 'Open purchase orders against expenses actually recorded, per vendor.',
    methodology: 'Open purchase-order value is a commitment, not a cost. Expenses created from those orders are shown beside it so nothing is counted twice.',
    sources: ['purchaseOrder', 'expense'],
    permissions: ['purchaseOrder:read', 'expense:read'],
    filters: ['period', 'vendorId', 'currency'],
    columns: [col('vendor', 'Vendor'), money('committed', 'Open commitments'), money('expensed', 'Recorded as expense'), money('approved', 'Approved & paid')],
    drilldown: 'Vendor → vendor page',
  },
];

export const BLOCKED_REPORTS: ReadonlySet<string> = new Set([
  'receivables_retainer_invoice_details',
  'receivables_delivery_challan_details',
  'inventory_inventory_summary',
  'inventory_inventory_valuation_summary',
  'inventory_fifo_cost_lot_tracking',
  'inventory_inventory_aging_summary',
  'inventory_inventory_adjustment_summary',
  'inventory_inventory_adjustment_details',
  'payables_bills_details',
  'payables_recurring_bills_details',
  'payables_vendor_credits_details',
  'payables_refund_history',
  'payables_purchase_return_summary',
  'payables_purchase_return_details',
  'expenses_expenses_by_project',
  'expenses_billable_expense_details',
  'expenses_mileage_expenses_by_employee',
  'projects_project_summary',
  'projects_project_revenue_summary',
  'projects_timesheet_details',
  'recurring_recurring_invoice_details',
  'taxes_tds_receivables_summary',
  'taxes_tds_payables_summary',
  'taxes_tcs_summary',
  'taxes_reverse_charge_summary',
  'taxes_sales_reverse_charge_summary',
]);

export const REGION_DISABLED_REPORTS: ReadonlySet<string> = new Set([
  'taxes_overseas_tax_summary',
  'taxes_vat_moss_report',
  'taxes_oss_ioss_scheme_report',
  'taxes_fec_report',
]);

/** Reports whose calculation and output work against real data, awaiting their own tests. */
export const IMPLEMENTED_REPORTS: ReadonlySet<string> = new Set<string>([]);

/** Where each report's tests live, relative to apps/api/test. A test file must name the report ids it executes. */
const TEST_FILES: [RegExp, string][] = [
  [/^(sales_|inventory_abc)/u, 'integration/reports-sales.test.ts'],
  [/^(receivables_|payments_received_)/u, 'integration/reports-receivables.test.ts'],
  [/^(payables_|expenses_|whn_|taxes_|activity_)/u, 'integration/reports-payables.test.ts'],
  [/^(business_|accountant_)/u, 'integration/reports-ledger.test.ts'],
];
const testFilesFor = (id: string): string[] => [...TEST_FILES.filter(([pattern]) => pattern.test(id)).map(([, file]) => file), 'integration/reports-export.test.ts'];

/** Reports executed by a test that names them. Maintained alongside the tests. */
export const TESTED_REPORTS: ReadonlySet<string> = new Set<string>([
  'sales_by_customer', 'sales_by_item', 'sales_by_sales_person', 'sales_summary',
  'inventory_abc_classification',
  'business_profit_and_loss', 'business_cash_flow_statement', 'business_balance_sheet', 'business_performance_ratio', 'business_movement_of_equity', 'business_cash_flow_forecast',
  'accountant_account_transactions', 'accountant_account_type_summary', 'accountant_general_ledger', 'accountant_detailed_general_ledger', 'accountant_trial_balance', 'accountant_journals', 'accountant_daybook', 'accountant_budgets_vs_actuals', 'accountant_13th_month_trial_balance', 'accountant_horizontal_profit_and_loss',
  'taxes_tax_summary',
  'activity_activity_logs',
  'receivables_customer_balance_summary', 'receivables_aging_summary', 'receivables_aging_details', 'receivables_invoice_details', 'receivables_sales_order_details', 'receivables_quote_details', 'receivables_receivable_summary', 'receivables_receivable_details',
  'payments_received_payments_received', 'payments_received_time_to_get_paid', 'payments_received_credit_note_details', 'payments_received_refund_history',
  'payables_vendor_balance_summary', 'payables_aging_summary', 'payables_aging_details', 'payables_payments_made', 'payables_purchase_order_details', 'payables_purchase_orders_by_vendor', 'payables_purchase_order_by_item', 'payables_purchases_by_vendor', 'payables_purchases_by_item', 'payables_payable_summary', 'payables_payable_details',
  'expenses_expense_details', 'expenses_expenses_by_category', 'expenses_expenses_by_customer', 'expenses_expenses_by_employee', 'expenses_recurring_expense_details',
  'whn_gst_invoice_details', 'whn_expense_report_details', 'whn_expense_report_summary', 'whn_credit_note_reconciliation', 'whn_bank_account_summary', 'whn_vendor_commitments',
]);

function statusFor(id: string): ReportStatus {
  if (BLOCKED_REPORTS.has(id)) return 'BLOCKED_BY_MISSING_MODULE';
  if (REGION_DISABLED_REPORTS.has(id)) return 'DISABLED_BY_REGION';
  if (TESTED_REPORTS.has(id)) return 'TESTED';
  if (IMPLEMENTED_REPORTS.has(id)) return 'IMPLEMENTED';
  return 'IN_PROGRESS';
}

function build(): { standard: CatalogueReport[]; whn: CatalogueReport[] } {
  const standard: CatalogueReport[] = [];
  let number = 0;
  REPORT_CATEGORIES.forEach((category, categoryIndex) => {
    if (category.id === 'whn_books') return;
    ORDERED[category.id].forEach((spec, index) => {
      number += 1;
      standard.push({
        ...spec,
        category: category.id,
        categoryPosition: categoryIndex + 1,
        position: index + 1,
        catalogueNumber: number,
        orientation: spec.orientation ?? 'portrait',
        exports: ALL_EXPORTS,
        status: statusFor(spec.id),
        testRef: TESTED_REPORTS.has(spec.id) ? testFilesFor(spec.id) : [],
      });
    });
  });
  const whnIndex = REPORT_CATEGORIES.findIndex((category) => category.id === 'whn_books');
  const whn = WHN_SPECS.map(
    (spec, index): CatalogueReport => ({
      ...spec,
      category: 'whn_books',
      categoryPosition: whnIndex + 1,
      position: index + 1,
      catalogueNumber: 0,
      orientation: spec.orientation ?? 'portrait',
      exports: ALL_EXPORTS,
      status: statusFor(spec.id),
      testRef: TESTED_REPORTS.has(spec.id) ? testFilesFor(spec.id) : [],
    }),
  );
  return { standard, whn };
}

const built = build();

/** The 81 standard reports, in catalogue order. */
export const STANDARD_REPORTS: readonly CatalogueReport[] = built.standard;
/** WHNUNIVERSE-specific reports, kept separate so they never inflate the count. */
export const WHN_REPORTS: readonly CatalogueReport[] = built.whn;
export const ALL_REPORTS: readonly CatalogueReport[] = [...built.standard, ...built.whn];

const BY_ID = new Map(ALL_REPORTS.map((report) => [report.id, report]));
export const reportById = (id: string): CatalogueReport | undefined => BY_ID.get(id);
